import _ from 'lodash';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Segment } from 'semantic-ui-react';
import { FormContext } from '../../cnr/contexts/FormContext';
import { formatItem, formatTypes } from '../../common/dateFormatting';
import { filterHelpers } from '../../common/filtering';
import { uniqueKey } from '../../common/keys';
import { dynListTypes, gEnums, listTypes } from '../../enums/globalEnums';
import CheckboxInput from './elements/CheckboxInput';
import DateInput from './elements/DateInput';
import DropdownInput from './elements/DropdownInput';
import FormInput from './elements/FormInput';
import { ImageInput } from './elements/ImageInput';
import NumberPicker from './elements/NumberPicker'
import SelectInput from './elements/SelectInput';
import TextAreaInput from './elements/TextAreaInput';
import { formItemProps } from './props/formItemProps';
import { getFormOptions } from './props/formOptions';
import { listHelpers } from 'global/lists/lists';
import { SquaresContext } from 'global/wrappers/SquaresWrapper';
import { useDynamicListOptions } from 'global/cnr/contexts/useHelpers/optionsBuilder/useDynamicListOptions';

const AddressForm = React.lazy(() => import('./AddressForm'));
const AppUserDataPermissions = React.lazy(() => import('./elements/AppUserDataPermissions'));
const DataConstraintList = React.lazy(() => import('./elements/DataConstraintList'));
const DropdownLabel = React.lazy(() => import('./elements/DropdownLabel'));
const GenericCard = React.lazy(() => import('../../genericControls/GenericCard'));
const GenericList = React.lazy(() => import('../../genericControls/GenericList'));
const GenericProfile = React.lazy(() => import('../../genericControls/GenericProfile'));
const GolfTeams = React.lazy(() => import('../../../projectSpecific/golf/elements/GolfTeams'));
const InlineFormElements = React.lazy(() => import('./groupedElements/InlineFormElements'));
const ItemList = React.lazy(() => import('./groupedElements/ItemList'));
const ItemMap = React.lazy(() => import('./groupedElements/ItemMap'));
const ListAdd = React.lazy(() => import('../../lists/ListAdd'));
const ManifestPreview = React.lazy(() => import('../../manifest/ManifestPreview'));
const MenuItemsSelect = React.lazy(() => import('./elements/MenuItemsSelect'));
const NumberPadPicker = React.lazy(() => import('./elements/NumberPadPicker'));
const PhoneNumberInput = React.lazy(() => import('./elements/PhoneNumberInput'));
const PropGroup = React.lazy(() => import('./elements/PropGroup'));
const SemFileViewer = React.lazy(() => import('../sem/SemFileViewer'));
const SignIn = React.lazy(() => import('../auth/SignIn'));
const Splash = React.lazy(() => import('../alerts/Splash'));
const TableMap = React.lazy(() => import('./elements/TableMap'));
const TextConfig = React.lazy(() => import('./elements/TextConfig'));
const ThemedColors = React.lazy(() => import('./elements/ThemedColors'));
const UploadInput = React.lazy(() => import('./elements/UploadInput'));

const logErrors = true

const _phoneExceptions = ['displayPhoneNumber', 'allowPhoneNumber', 'hidePhoneNumber', 'hidePhoneNumberFromApp', 'useClientProfilePhoneNumber']


export const FormItemContext = createContext();

/**
 * 
 * @param {*} props (activeItem, allow, index, itemProp, propKey, readOnlyForm)
 * @returns a single formItem (element) based on the `itemProp`
 */
const FormItem = (props) => {

  const {
    activeItem,
    allow,
    index,
    itemProp,
    propKey,
    readOnlyForm,
    viewOnly,
  } = props

  const { data, key } = itemProp ?? {}
  const { listType: dynListType, listFrom, staticView: staticView_data, staticListProp: staticListProp_data, formItemType } = data ?? {}

  // formContext
  const formContext = useContext(FormContext)
  const { form_state, form_handlers, swipeToSelected, swipedItem, lockedData, formParent_contexts } = formContext ?? {}
  const { formKey, data_localForm, formProps, optionsList, modifyActionType } = form_state ?? {}
  const { handlePropPush, handleEditor, handleFormItemChange } = form_handlers ?? {}
  const { subKey, data_init, vit, updateProps, form_handlers: form_handlers_props } = formProps ?? {}
  let { itemKey, isReadOnly } = formProps

  // formParent_contexts
  const { states, settings } = formParent_contexts ?? {}
  const { appUser_state, eventInfo_state, currentSettings_state, help_state, appSettings_state } = states ?? {}
  const { appUser: authAppUser } = appUser_state ?? {}
  const { aps_global, aps_viewItems, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appSettings, themeColors, dataOptions } = aps_global ?? {}
  const { clientRegistrationUrl, useClientRegistrationUrl } = appSettings ?? {}
  const { dateFormat, forceCollectionDropdown } = dataOptions ?? {}
  const { staticViews, appDataDocuments } = eventInfo_state ?? {}
  const { helpOn } = appSettings_state ?? {}
  const { helpDocs } = help_state ?? {}
  const { helpDocs: _helpDocs } = helpDocs ?? {}

  const { additionalAppUserViewTypeProps, appUserCollection, appUserViewTypeProp } = aps_appUserSettings ?? {}
  const viewItem_appUser = appUserCollection && aps_viewItems && aps_viewItems[appUserCollection] ? aps_viewItems[appUserCollection] : {}
  const { props: props_appUser } = viewItem_appUser ?? {}

  const squaresContext = useContext(SquaresContext)
  const { squares_state } = squaresContext ?? {}
  const { view: view_ss } = squares_state ?? {}

  const _propHelp = helpOn && _helpDocs && _helpDocs[propKey] ? _helpDocs[propKey] : null

  const _viKeys = aps_viewItems ? Object.keys(aps_viewItems) : []

  const { homeSettings } = settings ?? {}
  const { global: global_home } = homeSettings ?? {}
  const { globalAppSettings } = global_home ?? {}
  const { registrationUrl } = globalAppSettings ?? {}

  const regUrl = useClientRegistrationUrl && clientRegistrationUrl ? clientRegistrationUrl : registrationUrl

  const { getDynOption } = useDynamicListOptions()

  const [currentFormItemProps, setCurrentFormItemProps] = useState()

  const getFormDataTypes = (value, formItemType, formItemTypeModified) => {
    //  return formItemType
    // console.log('value', value)
    // if (_.isString(value) && !_.isEmpty(value)) return gEnums.formItemTypes.text;
    // if (_.isArray(value)) return gEnums.formItemTypes.dropdown;

    // if (_.isBoolean(value)) return gEnums.formItemTypes.checkbox;
    // if (_.isNumber(value)) return gEnums.formItemTypes.slider;
    // if (_.isDate(value)) return gEnums.formItemTypes.date;
    // if (_.isObject(value)) return formItemType;
    // if ((formItemType === gEnums.formItemTypes.select)) return gEnums.formItemTypes.dropdown;
    return formItemTypeModified || formItemType;
  };

  const { propOptions, formItemTypeModified } = useMemo(() => {
    return getDynOption(itemProp, data_localForm, view_ss, squares_state)
  }, [data_localForm, itemProp])

  const form_item_props = useMemo(() => {
    const { data, display: display_itemProp } = itemProp ?? {}

    let {
      appQuestion,
      defaultToTodayIfDate,
      formItemType,
      listType,
      listFrom,
      required,
      staticList,
      staticPropFilter,
      staticPropFilterValue,
      suffix,
    } = data ?? {}

    let { caption } = display_itemProp ?? {}
    let captionQuestion;

    if (appQuestion) {
      if (globalAppSettings && globalAppSettings[appQuestion]) {
        captionQuestion = globalAppSettings[appQuestion]
      }
    }

    let truePropKey = propKey
    caption = caption ? caption : _.startCase(truePropKey)
    formItemType = formItemType || gEnums.formItemTypes.text


    if (caption && staticList && staticPropFilter && staticPropFilterValue && caption.indexOf(staticList) < 0) {
      caption += ' (' + _.startCase(staticList) + '/' + _.startCase(staticPropFilter) + '/' + _.startCase(staticPropFilterValue) + ')'
    }

    if (caption && suffix && caption.indexOf(suffix) < 0) {
      caption += ' (' + suffix + ')'
    }

    switch (formItemType) {
      case 'label':
        formItemType = gEnums.formItemTypes.text
        break;
      default:
      // nothing
    }

    const isEdit = (modifyActionType === gEnums.dataUpdateTypes.updateDoc)

    const fieldProps = {
      key: itemProp.as ? itemProp.as + '-' + truePropKey + '-' + index : truePropKey + '-' + index,
      caption: caption,
      disabled: itemProp.disabled || !allow ? true : null,
      form_handlers: form_handlers_props,
      formitemtype: formItemType,
      itemKey: itemKey,
      itemProp: itemProp,
      label: caption,
      onChange: handleFormItemChange,
      opts: itemProp.opts,
      parentkey: formKey,
      propkey: truePropKey,
      propname: truePropKey,
      readOnly: (itemProp.readOnly && isEdit) || itemProp.viewOnly || viewOnly ? true : false,
      subkey: subKey,
      vit: vit,
    }

    if (itemProp && itemProp.data && itemProp.data.readOnly && isEdit) { fieldProps.readOnly = true }
    if (lockedData && lockedData.includes(truePropKey)) { fieldProps.disabled = true }
    if (readOnlyForm && isEdit) { fieldProps.readOnly = true }

    if (isReadOnly) { fieldProps.readOnly = true }

    // set the defaults
    switch (formItemType) {
      case gEnums.formItemTypes.upload:
        if (!fieldProps.value) { fieldProps.value = '' }
        break;
      case gEnums.formItemTypes.textArea:
      case gEnums.formItemTypes.textAreaSimple:
        if (!fieldProps.value) { fieldProps.value = '' }
        break;
      case gEnums.formItemTypes.checkbox:
        if (!fieldProps.value) { fieldProps.value = false }
        break;
      default:
        if (!fieldProps.value) { fieldProps.value = '' }
      // nothing
    }

    const tpklc = truePropKey.toLowerCase()

    if (tpklc.indexOf('password') >= 0) {
      fieldProps.type = 'password'
      fieldProps.otype = 'password'
    }

    const fip = formItemProps(fieldProps, handleFormItemChange, required, _propHelp)

    if (tpklc.endsWith('active')) {
      formItemType = gEnums.formItemTypes.checkbox
      itemProp.data.formItemType = gEnums.formItemTypes.checkbox
    }

    if (tpklc.indexOf('date') >= 0 &&
      tpklc.endsWith('date') &&
      !tpklc.endsWith('update') &&
      tpklc.indexOf('format') < 0 &&
      tpklc.indexOf('menus') < 0 &&
      tpklc.indexOf('horizontalIsDate') < 0) {
      formItemType = gEnums.formItemTypes.date
    }

    if (tpklc.endsWith('year') || tpklc.endsWith('latestseason')) {
      formItemType = gEnums.formItemTypes.year
      itemProp.data.formItemType = gEnums.formItemTypes.select
    }

    if (tpklc.indexOf('phonenumber') >= 0 && !_phoneExceptions.includes(truePropKey)) {
      formItemType = gEnums.formItemTypes.phoneNumber
    }

    if (fieldProps.value && _.isObject(fieldProps.value)) {

      switch (formItemType) {
        case gEnums.formItemTypes.text:
          formItemType = gEnums.formItemTypes.dropdownSelect
          break;

        case gEnums.formItemTypes.itemArrayMulti:
        case gEnums.formItemTypes.dropdown:
        case gEnums.formItemTypes.dropdownAdd:
        case gEnums.formItemTypes.dropdownGroup:
        case gEnums.formItemTypes.listSelect:
          // nothing
          break;
        default:
        // nothing
      }
    }

    switch (formItemType) {
      case gEnums.formItemTypes.select:
        if (fieldProps.value && _.isArray(fieldProps.value)) {
          formItemType = gEnums.formItemTypes.dropdown
        }
        if (forceCollectionDropdown && _viKeys && _viKeys.includes(truePropKey)) {
          formItemType = gEnums.formItemTypes.dropdown
        }
        break;
      default:
      // nothing
    }

    const optionsProps = { itemProp, optionsList, data_init }
    const fipProps = { fip, itemProp, regUrl, form_handlers_props, handlePropPush }

    delete fip.formItemType

    return {
      captionQuestion,
      data,
      defaultToTodayIfDate,
      fieldProps,
      fip,
      fipProps,
      formItemType,
      optionsProps,
      truePropKey,
      itemProp,
      themeColors,
      _propHelp,
    }
  }, [data_localForm]);

  const form_item_value = useMemo(() => {

    const { data, parents } = itemProp ?? {}
    let { eventKey } = data ?? {}

    let truePropKey = propKey
    let propValue = null

    try {
      if (data_localForm) {
        propValue = eventKey && (truePropKey !== eventKey) ? data_localForm[eventKey] : data_localForm[truePropKey]
      }
    } catch (error) {
      logErrors && console.error('ERROR', error)
    }

    if (data_localForm && parents) {
      let cd = data_localForm
      itemProp.parents.forEach(parent => {
        if (cd) { cd = cd[parent] }
      })
      propValue = cd ? cd[truePropKey] : null
    }

    if (itemProp && itemProp.data && itemProp.data.propFrom && itemProp.data.propFromProp) {
      switch (itemProp.data.propFrom) {
        case 'appUser':
          if (authAppUser && authAppUser[itemProp.data.propFromProp]) {
            propValue = authAppUser[itemProp.data.propFromProp]
          }
          break;
        default:
        // nothing
      }
    }

    return propValue
  }, [data_localForm]);

  const form_item_item = useMemo(() => {

    if (currentFormItemProps) {
      const {
        captionQuestion,
        data,
        defaultToTodayIfDate,
        fieldProps,
        fip,
        fipProps,
        formItemType,
        optionsProps,
        truePropKey,
      } = currentFormItemProps

      const _optionsList = optionsList //|| propOptions

      const _formItemType = getFormDataTypes(fip.value, formItemType, formItemTypeModified)

      switch (_formItemType) {

        case gEnums.formItemTypes.dataLink:
          return <div>DATA LINK</div>

        case gEnums.formItemTypes.image:
          return <ImageInput />

        case gEnums.formItemTypes.address:
          return <AddressForm />

        case gEnums.formItemTypes.authAppUserTypes:
          return <div>authAppUserTypes</div>

        case gEnums.formItemTypes.inlineItems:
          return <InlineFormElements
            key={uniqueKey('fi', 'ife')}
            activeItem={activeItem}
          />
        case gEnums.formItemTypes.checkbox:
          if (!fieldProps.value) { fieldProps.value = false }
          return <CheckboxInput captionQuestion={captionQuestion} />

        case gEnums.formItemTypes.backgroundColorSelectSingle:
        case gEnums.formItemTypes.colorSelect:
        case gEnums.formItemTypes.colorSelectSingle:
          return <ThemedColors key={uniqueKey('fi', 'tc')} fip={fip} themeColors={themeColors} itemProp={itemProp} />

        case gEnums.formItemTypes.dropdown:
        case gEnums.formItemTypes.dropdownAdd:
        case gEnums.formItemTypes.listSelect:

          try {
            const { data: prop_data } = itemProp ?? {}
            return <DropdownInput fip={fip} optionList={prop_data.trueList || propOptions} selectMulti={true} itemProp={itemProp} />
          } catch (error) {
            logErrors && console.error('itemProp', itemProp, error)
          }
          break;

        case gEnums.formItemTypes.dropdownGroup:
          try {
            if (_optionsList) {
              let _optionList = getFormOptions(optionsProps)
              return <PropGroup dropdown={<DropdownInput fip={fip} optionList={_optionList} selectMulti={true} />} />
            } else {
              return <DropdownInput fip={fip} optionList={[]} selectMulti={true} />
            }
          } catch (error) {
            logErrors && console.error('itemProp', itemProp, error)
          }
          break;

        case gEnums.formItemTypes.gridRowSelect:
        case gEnums.formItemTypes.gridRowSelectFull:
          let optionList = itemProp && itemProp.data && itemProp.data.trueList ? itemProp.data.trueList : getFormOptions(optionsProps)
          return <MenuItemsSelect menuList={optionList} />

        case gEnums.formItemTypes.menuItemSelect:
        case gEnums.formItemTypes.menuItemSelectFull:
        case gEnums.formItemTypes.menuItemSelectFullCount:
          let menuList = itemProp && itemProp.data && itemProp.data.trueList ? itemProp.data.trueList : getFormOptions(optionsProps)
          return <MenuItemsSelect menuList={menuList} />

        case gEnums.formItemTypes.itemMap:
        case gEnums.formItemTypes.itemArrayMulti:
          return <ItemMap fip={fip} formItemType={_formItemType} themeColors={themeColors} labelProps={itemProp.data ? itemProp.data.labelProps : null} />

        case gEnums.formItemTypes.itemList:
          return <ItemList
            documentData={data_localForm}
            updateProps={updateProps}
          />

        case gEnums.formItemTypes.fileItemSelect:
        case gEnums.formItemTypes.fileItemSelectDirect:
          return <SemFileViewer fileMode={gEnums.fileModes.viewWithSelectDirect} noMid={true} />

        case gEnums.formItemTypes.fileManifestSelect:
          return <SemFileViewer fileMode={gEnums.fileModes.viewManifest} noMid={true} />

        case gEnums.formItemTypes.fileSelect:
          return <SemFileViewer fileMode={gEnums.fileModes.viewWithSelect} noMid={true} />
        // return <SemFileViewer fip={fip} fieldProps={fieldProps} formProps={formProps} fileMode={gEnums.fileModes.viewWithSelect} />

        case gEnums.formItemTypes.listAdd:
        case gEnums.formItemTypes.listView:
          return <ListAdd inverted={true} />

        case gEnums.formItemTypes.appUserDataConstraintList:
        case gEnums.formItemTypes.dataConstraintList:
        case gEnums.formItemTypes.dataMatchConstraintList:
        case gEnums.formItemTypes.pageConstraintList:
        case gEnums.formItemTypes.imagePropList:
          return <DataConstraintList />

        case gEnums.formItemTypes.appUserDataModificationList:
          return <AppUserDataPermissions />

        case gEnums.formItemTypes.dropdownSelect:
          return <DropdownLabel
            arrayValue={fieldProps.value}
            forEdit={true}
            isAppData={true}
            propKey={truePropKey}
            staticViews_app={staticViews}
          ></DropdownLabel>


        case gEnums.formItemTypes.select:
        case gEnums.formItemTypes.nameSelect:
        case gEnums.formItemTypes.year:

          try {
            if (_optionsList) {
              let optionList = itemProp && itemProp.data && itemProp.data.trueList ? itemProp.data.trueList : getFormOptions(optionsProps)
              if (optionList && fip.value && _.isArray(fip.value)) {
                if (itemProp && itemProp.data && itemProp.data.staticList && staticViews && staticViews[itemProp.data.staticList]) {
                  const svc = staticViews[itemProp.data.staticList]
                  const newV = []
                  fip.value.forEach(v => {
                    const x = _.find(svc, { 'name': v })
                    if (x) { newV.push(x.key) }
                  })
                  if (newV.length > 0) { fip.value = newV }
                }
              }
              return <SelectInput fip={fip} itemProp={itemProp} optionList={optionList} listType={itemProp.listType} fipProps={fipProps} />
            } else {
              if (itemProp?.data?.trueList) {
                return <SelectInput fip={fip} itemProp={itemProp} optionList={itemProp.data.trueList} listType={itemProp.listType} />
              } else {
                return <SelectInput fip={fip} itemProp={itemProp} optionList={propOptions} listType={itemProp.listType} />
              }
            }
          } catch (error) {
            logErrors && console.error('itemProp', itemProp, truePropKey, error)
          }

          return <SelectInput fip={fip} itemProp={itemProp} optionList={[]} />

        case gEnums.formItemTypes.numberPicker:
          return <NumberPadPicker fip={fip} itemProp={itemProp} />

        case gEnums.formItemTypes.slider:
          // console.log('form_item_value', itemProp?.key, _formItemType, form_item_value, fip)
          // console.log('currentFormItemProps', currentFormItemProps)
          return <NumberPicker fip={fip} itemProp={itemProp} />

        case gEnums.formItemTypes.phoneNumber:
          return <PhoneNumberInput />

        case gEnums.formItemTypes.tableMap:
          const tmProps = { fip, itemProp, data_init, swipedItem, swipeToSelected }
          return <TableMap tmProps={tmProps} />

        case gEnums.formItemTypes.date:
          if (!fip.value && defaultToTodayIfDate) {
            const d = new Date()
            const dd = formatItem(formatTypes.date, d)
            fip.value = dd
          }
          return <DateInput fip={fip} itemProp={itemProp} dateFormat={dateFormat} />

        case gEnums.formItemTypes.text:
        case gEnums.formItemTypes.textReadOnly:
        case gEnums.formItemTypes.autoFill:
          return <FormInput ipProps={fipProps} />

        case gEnums.formItemTypes.textArea:
        case gEnums.formItemTypes.textAreaSimple:
          if (!fieldProps.value) { fieldProps.value = '' }
          return <TextAreaInput fip={fip} itemProp={handleEditor} formItemType={_formItemType} />

        case gEnums.formItemTypes.textConfig:
          return <TextConfig fip={fip} />

        case gEnums.formItemTypes.upload:
          if (!fieldProps.value) { fieldProps.value = '' }
          return <UploadInput fip={fip} form_handlers={form_handlers_props} />

        default:
          const { previewType } = data ?? {}
          switch (previewType) {
            case gEnums.previewTypes.display:
              return <div>DISPLAY</div>
            case gEnums.previewTypes.manifest:
              return <ManifestPreview formData={data_localForm} />
            case gEnums.previewTypes.splashScreen:
              return <Splash formData={data_localForm} />
            case gEnums.previewTypes.signIn:
              return <SignIn />
            case gEnums.previewTypes.golferTeamSelect:
              return <GolfTeams forSelect={true} />
            case gEnums.previewTypes.dataFilters:
              return <Segment inverted>dataFilters</Segment>
            case gEnums.previewTypes.list:
              return <GenericList formData={data_localForm} itemProp={itemProp} subKey={subKey} />
            case gEnums.previewTypes.card:
            case gEnums.previewTypes.header:
            case gEnums.previewTypes.sectionBody:
            case gEnums.previewTypes.sectionHeader:
            case gEnums.previewTypes.profile:
              switch (subKey) {
                case 'description':
                case 'header':
                case 'meta':
                  return <GenericProfile formData={data_localForm} itemProp={itemProp} subKey={subKey} />
                default:
                  return <GenericCard formData={data_localForm} itemProp={itemProp} />
              }
            default:
              return <div>{'No available form item settings for ' + _formItemType}</div>
          }
        // nothing
      }
    }

  }, [currentFormItemProps, formKey, propOptions]);

  useEffect(() => {
    if (form_item_props) {
      form_item_props.fip.value = form_item_value
      setCurrentFormItemProps(form_item_props)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form_item_value]);

  return <FormItemContext.Provider value={{ formItem_state: { ...currentFormItemProps } }}>
    {currentFormItemProps && form_item_item}
  </FormItemContext.Provider>

}

export default FormItem